import { COIN_TOP_HAT, MONTHLY_MOUSTACHE, PUB_BEARD, STREAK_TOP_HAT } from "./dynamic-hats";
import { weeklyDrop } from "./item-availability";
import { ShopItem } from "./types";

/**
 * The shop's item catalogue. This is the source of truth for item
 * definitions across all environments (test and live). Add or edit items
 * here rather than through Firestore.
 *
 * `id` matches each item's Firestore document id as exported from production
 * on 2026-09-16, and must stay stable: it's baked into every player's
 * purchase history (`UserProfile.purchases`), inventory rows, and equipped
 * state, so renaming or removing an id here orphans those existing
 * references rather than cleanly migrating them.
 *
 * Prices follow how good an item is rather than its type: plain emoji hats
 * are cheap starters, drawn image hats cost more, and status and joke hats
 * are long-term goals; effects go from static filters up to the eye-catching
 * animations. A spite's `price` buys its shortest run (`durationHours`, 15–30
 * minutes) and is set by how badly it gets in the way: purely cosmetic ones
 * are pocket change and can be stretched to 8 hours, while ones that make the
 * app barely usable cost the most and stop at an hour. Buyers pay more to
 * stretch a spite up to `maxDurationHours`; see spite-utils.ts.
 * economy-sim.test.ts checks the catalogue stays affordable. Crude joke hats
 * marked `options.novelty` are left out of its everyday-looks budget, so they
 * can be added without repricing everything else.
 *
 * `availability` limits when an item is on sale (see item-availability.ts):
 * seasonal ones like Halloween and Christmas come back every year, one-off
 * event hats are on sale for a day or a week and are promoted at the top of
 * the shop (the Movember Moustache comes back every Movember, and the Party Hat
 * the week of everyone's birthday), and the hats, spites, curses, defences,
 * power-ups and gambles added in October 2026 are dropped a few at a time, on
 * Thursdays at 5pm, so there's something new every week (see releases.ts for
 * what's in each week). Items are tagged as new for their first week on sale.
 *
 * `conditions` (see item-conditions.ts) limit when and where an item in the
 * shop can be bought: the other five October 2026 hats are secrets that only
 * sell at certain times of night or at pubs a long way from Cardiff.
 *
 * Gambling items (`type: "gambling"`) additionally track a live, changing
 * `value` (the running jackpot) in Firestore at `/itemState/{id}`; the
 * `value` below is only the starting point. See getShopItemWithState in
 * functions/src/actions/shop.ts.
 */
export const SHOP_ITEMS: ShopItem[] = [
  {
    id: "139YGuTqG5LFUGw8wJYA",
    name: "Custom Hat",
    description:
      "Choose your own image to wear as a hat. The image is fixed once bought, but you can buy as many custom hats as you like",
    type: "hat",
    // Left blank on purpose: each purchase takes the buyer's uploaded image
    // as the new hat's value (see handleHatPurchase).
    value: "",
    price: 10000,
    active: true,
    options: { type: "customHat" },
  },
  {
    id: "504mB5PUY6IcLI8TGo2L",
    name: "Ribbon 🎀",
    description: "Adds a ribbon to your avatar",
    type: "hat",
    value: "🎀",
    price: 200,
    active: true,
  },
  {
    id: "5t5Oj6ACFhjtoF9O0VHl",
    name: "Reverse",
    description: "Reverses everything horizontally.",
    type: "spite",
    value: "spite-reverse",
    price: 60,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "9xpaXMs5DJolKMUois0R",
    name: "Military Helmet 🪖",
    description: "Adds a helmet to your avatar",
    type: "hat",
    value: "🪖",
    price: 100,
    active: true,
  },
  {
    id: "AIegH6PowSXH3SeNWkUT",
    name: "Ḑ̵̧̝͚̝̗͉̓͌͗̈̀͘͝͝ë̴͚̬̪̩̱͍͔́̍̆̈́̾̽̈́͐̽́̿͗͂͘e̵̡̧̛̮̙̜̙̓̂̄̄̃̈́͛̀̍̚p̴̜̟̲̜̥̅̇̃̌̑ ̴̛͙̭̪̍̒̇̈́͋͊͒́̃̓͠F̷̨̝̖̺͂̒͌̿̓͗̕ͅr̶̜̍͑̀̈̔̏y̴̛͕̠̠͇͖̤̟͚̜̖̳͉̗̭̘͆̍͐̍́͊͘ 💦",
    description: "Deep fries your avatar 😂👌",
    type: "effect",
    value: "effect-deep-fry",
    price: 1500,
    active: true,
  },
  {
    id: "Bu6woxOXOPS44Tjn9nA5",
    name: "Squash",
    description: "Squashes the screen vertically.",
    type: "spite",
    value: "spite-squash",
    price: 15,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 8 },
  },
  {
    id: "C6lOlzAFNGupzD11zphk",
    name: "Cowboy Hat",
    description: "Adds a cowboy hat to your avatar",
    type: "hat",
    value: "https://pngimg.com/uploads/cowboy_hat/cowboy_hat_PNG72.png",
    price: 600,
    active: true,
  },
  {
    id: "CzWVoA8Gf9nkOsVKaXZI",
    name: "Spookify",
    description: "👻 Makes your avatar all spooky 👻",
    type: "effect",
    value: "effect-ghost",
    price: 1000,
    active: true,
  },
  {
    id: "E2xbADOVkr0HYAqnKe2f",
    name: "Graduation Cap 🎓",
    description: "Adds a graduation cap to your avatar",
    type: "hat",
    value: "🎓",
    price: 100,
    active: true,
  },
  {
    id: "EzVkhacEsKaogmUHVc9F",
    name: "Blur",
    description: "Blurs the spited's screen a little bit.",
    type: "spite",
    value: "spite-blur",
    price: 25,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4 },
  },
  {
    id: "HDQMuhLuMvZAm17gYuHO",
    name: "Hat Inverter",
    description: "Inverts the colour of your hat for some reason",
    type: "effect",
    value: "effect-invert-hat",
    price: 100,
    active: true,
  },
  {
    id: "KEzeIwsyHJYkCHGXmftI",
    name: "Chef Hat",
    description: "Adds a chef hat to your avatar ",
    type: "hat",
    value:
      "https://static.vecteezy.com/system/resources/previews/026/757/704/non_2x/chef-hat-with-ai-generated-free-png.png",
    price: 500,
    active: true,
  },
  {
    id: "KUqkB8pczzkR0MnrCeDK",
    name: "Bouncer",
    description: "Bounces your avatar up and down",
    type: "effect",
    value: "animate-bounce",
    price: 300,
    active: true,
  },
  {
    id: "LDLYLMSYDCVNwMbgHYoF",
    name: "🥚 Egg 🥚",
    description: "Drops some eggs all over the screen 🥚",
    type: "spite",
    value: "spite-egg",
    price: 75,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "LXu74lwYzOCQTKwpLkmD",
    name: "Earthquake",
    description: "Start an earthquake, makes the app very hard to use.",
    type: "spite",
    value: "spite-earthquake",
    price: 75,
    active: true,
    options: { durationHours: 0.25, maxDurationHours: 1 },
  },
  {
    id: "MrQkwpt61auq7Xd0rVIA",
    name: "Hat Spin",
    description: "Spins your hat round and round",
    type: "effect",
    value: "animate-spin-hat",
    price: 500,
    active: true,
  },
  {
    id: "S6I7W1SUwcYk4qk4UN0e",
    name: "Squish",
    description: "Squishes the screen horizontally.",
    type: "spite",
    value: "spite-squish",
    price: 15,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 8 },
  },
  {
    id: "SyN2MKiVbfuz1kFVH7Mb",
    name: "Scarf 🧣",
    description: "Wraps a chunky knitted scarf round your neck, its tail flapping in the wind",
    type: "hat",
    value: "/images/scarf.svg",
    price: 200,
    active: true,
  },
  {
    id: "TE6cvyZEEqGS6GdExP7o",
    name: "Spin",
    description: "Spins the screen slowly. Buy more to spin faster!",
    type: "spite",
    value: "spite-spin",
    price: 50,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "TJx7S7baVepRJzky6vou",
    name: "Censor hat",
    description: "Censors your hat - is it rude?",
    type: "effect",
    value: "effect-censor-hat",
    price: 250,
    active: true,
  },
  {
    id: "UxX5TT6GAo4anHnv6oGf",
    name: "Earthquake",
    description: "EXTREME earthquake ",
    type: "effect",
    value: "animate-earthquake",
    price: 1200,
    active: true,
  },
  {
    id: "VcQ9WsL2p91p0Sv4bHKc",
    name: "Round the world",
    description: "Rotates your hat all over the shop",
    type: "effect",
    value: "animate-round-the-world",
    price: 800,
    active: true,
  },
  {
    id: "ZlCJpaoL2tzizlMkyyxX",
    name: "Rotate the check-in button",
    description: "Makes it hard to click Check In",
    type: "spite",
    value: "spite-checkin-avoider",
    price: 90,
    active: true,
    options: { durationHours: 0.25, maxDurationHours: 1 },
  },
  {
    id: "ZzPsa738zwdP4PhiQ5EG",
    name: "Flip",
    description: "Flips everything upside-down",
    type: "spite",
    value: "spite-flip",
    price: 50,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2, odds: "", type: "lotto" },
  },
  {
    id: "ciCidQ2aUK0iifhneh5t",
    name: "M'lady",
    description: "Tips your hat gently",
    type: "effect",
    value: "animate-tip-hat",
    price: 450,
    active: true,
  },
  {
    id: "doF343LcrE5W2ncajNFb",
    name: "Ḑ̵̧̝͚̝̗͉̓͌͗̈̀͘͝͝ë̴͚̬̪̩̱͍͔́̍̆̈́̾̽̈́͐̽́̿͗͂͘e̵̡̧̛̮̙̜̙̓̂̄̄̃̈́͛̀̍̚p̴̜̟̲̜̥̅̇̃̌̑ ̴̛͙̭̪̍̒̇̈́͋͊͒́̃̓͠F̷̨̝̖̺͂̒͌̿̓͗̕ͅr̶̜̍͑̀̈̔̏y̴̛͕̠̠͇͖̤̟͚̜̖̳͉̗̭̘͆̍͐̍́͊͘ 💦",
    description: "Deep fries the entire screen 😂👌",
    type: "spite",
    value: "spite-deep-fry",
    price: 60,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "fbI4gyciTOaWgmvdYUlZ",
    name: "Confuse",
    description: "Flips a few things around and moves the layout around randomly.",
    type: "spite",
    value: "spite-confuse",
    price: 95,
    active: true,
    options: { durationHours: 0.25, maxDurationHours: 1 },
  },
  {
    id: "fjrJGCJK2rUsN8WiGpQs",
    name: "Skew",
    description: "Skews everything a little bit.",
    type: "spite",
    value: "spite-skew",
    price: 30,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4 },
  },
  {
    id: "hFwzyM3kuYe5lowg0805",
    name: "Egg Hat",
    description: "Adds an egg to your face",
    type: "hat",
    value: "🥚",
    price: 0,
    active: false,
  },
  {
    id: "iLNPGdXIxl6iGbH48dTY",
    name: "Invert",
    description: "Inverts every colour",
    type: "spite",
    value: "spite-invert",
    price: 5,
    active: false,
    options: { durationHours: 168 },
  },
  {
    id: "jxsUKSQJJ8qnx65Ofm8B",
    name: "Kimono 👘",
    description: "Dresses you in a silk kimono with a golden obi, cherry blossom drifting past",
    type: "hat",
    value: "/images/kimono.svg",
    price: 250,
    active: true,
  },
  {
    id: "lti9yspQAhGkSdgH3cq7",
    name: "Inverter",
    description: "Inverts your avatar",
    type: "effect",
    value: "effect-invert",
    price: 200,
    active: true,
  },
  {
    id: "nRpFibPa9p7maFzbFLQF",
    name: "☸️ Weekly Free Spin",
    description: "Spin the wheel for free once per week",
    type: "gambling",
    value: "100",
    price: 0,
    active: true,
    cooldownMinutes: 10080,
    options: { odds: ".25", type: "tombola", anywhere: true },
  },
  {
    id: "orlc6SA3Km8DHkxG6Ctg",
    name: "Pulser",
    description: "Subtly pulses your avatar",
    type: "effect",
    value: "animate-pulse",
    price: 150,
    active: true,
  },
  {
    id: "pymkSvsMHtikZwzZMiCP",
    name: "Safety Vest 🦺",
    description:
      "Puts you in a hi-vis vest with flashing reflective strips. Nobody will question why you're here",
    type: "hat",
    value: "/images/safety-vest.svg",
    price: 200,
    active: true,
  },
  {
    id: "qLSxnkLMqGjGSYZouowR",
    name: "Jazzy",
    description: "Makes all of the text pretty jazzy 🎷",
    type: "spite",
    value: "spite-jazzy",
    price: 35,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4 },
  },
  {
    id: "vOo2CjsPbgZbFdapIdZm",
    name: "Rescue Worker ⛑️",
    description: "Adds a rescue worker helmet to your avatar",
    type: "hat",
    value: "⛑️",
    price: 150,
    active: true,
  },
  {
    id: "wm0TDh2rg5GdUOZ9oi1O",
    name: "Censor",
    description: "Censors your avatar",
    type: "effect",
    value: "effect-censor",
    price: 400,
    active: true,
  },
  {
    id: "xJ81YvGV9dBZ5eUVL1jS",
    name: "Seasick",
    description: "Slides everything around a bit slowly, makes you queasy.",
    type: "spite",
    value: "spite-seasick",
    price: 30,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4 },
  },
  {
    id: "z4hUuoKxS6O5hIU5aEHP",
    name: "Crown 👑",
    description: "Adds a crown to your head, buy your way to power",
    type: "hat",
    value: "👑",
    price: 3000,
    active: true,
  },
  {
    id: "ztsbRNxtslssSAecPfps",
    name: "Broccoli Hat 🥦",
    description: "Adds some broccoli to your noggin",
    type: "hat",
    value: "🥦",
    price: 1000,
    active: true,
  },
  {
    id: "christmas",
    name: "Christmas 🎅",
    description:
      "🛒 A floppy Santa hat with a bouncing bobble, a big fluffy beard, rosy cheeks and falling snow. Ho ho ho!",
    type: "hat",
    value: "/images/santa.svg",
    price: 400,
    active: true,
    purchase: "constant",
    order: 1,
    limit: true,
    availability: { from: "12-01", to: "12-31", event: "Christmas" },
  },
  {
    id: "halloween",
    name: "Halloween 🎃",
    description: "🛒 Swaps your head for a flickering jack-o'-lantern, with a bat for company",
    type: "hat",
    value: "/images/pumpkin-head.svg",
    price: 250,
    active: true,
    purchase: "constant",
    order: 1,
    limit: true,
    availability: { from: "10-25", to: "10-31", event: "Halloween" },
  },
  {
    id: "invisiblehat",
    name: "Invisible Hat",
    description: "Adds an invisible hat to your profile picture.",
    type: "hat",
    value: "🥚",
    price: 1500,
    active: true,
    purchase: "constant",
    order: 7,
    limit: false,
  },
  {
    id: "doublecoins",
    name: "Double Coins",
    description:
      "🛠️ Earn twice the usual hourly coins for your current check in (bonuses aren't doubled)",
    type: "util",
    value: "",
    price: 50,
    // Retired: a sure ×2 was a plain coin printer. Kept so older
    // sessions and purchase histories still resolve it.
    active: false,
    cooldownMinutes: 720,
    options: { icon: "⏫" },
    purchase: "once",
    order: 150,
    limit: false,
  },
  {
    id: "spinner",
    name: "Spinner",
    description: "🛒 Spins your profile picture",
    type: "effect",
    value: "animate-spin",
    price: 350,
    active: true,
    purchase: "constant",
    order: 200,
    limit: true,
  },
  {
    id: "tophat",
    name: "Top Hat 🎩",
    description: "🛒 Adds a top hat to your profile picture",
    type: "hat",
    value: "🎩",
    price: 350,
    active: true,
    purchase: "constant",
    order: 250,
    limit: true,
  },
  {
    id: "baseballcap",
    name: "Baseball Cap 🧢",
    description: "🛒 Adds a baseball cap to your profile picture",
    type: "hat",
    value: "🧢",
    price: 150,
    active: true,
    purchase: "constant",
    order: 260,
    limit: true,
  },
  {
    id: "strawhat",
    name: "Straw Hat 👒",
    description: "🛒 Adds a straw hat to your profile picture",
    type: "hat",
    value: "👒",
    price: 100,
    active: true,
    purchase: "constant",
    order: 275,
    limit: true,
  },
  {
    id: "scratchcard",
    name: "Scratchcard",
    description: "💰 Buy to win a random coin prize",
    type: "gambling",
    value: "100",
    price: 3,
    active: true,
    options: { odds: "0.01", type: "scratchcard" },
    purchase: "once",
    order: 500,
    limit: false,
  },
  {
    id: "lotto",
    name: "Jackpot Lotto",
    description:
      "🎱 A ticket for the weekly draw, Thursdays at 7pm. The jackpot's only won a few times a year, so it rolls over and builds. Tickets, and a cut of every fine and lost bet, go in the pot",
    type: "gambling",
    value: "175",
    price: 5,
    active: true,
    // `odds` is each draw's chance of being won, whatever the tickets sold:
    // 1 in 16, about three times a year. See shared/lotto.ts.
    // A weekly ticket, so it's sold wherever you are.
    options: { odds: "0.0625", type: "lotto", anywhere: true, icon: "🎱" },
    purchase: "once",
    order: 505,
    limit: false,
    availability: weeklyDrop("2026-10-08"),
  },
  {
    id: "tombola",
    name: "Spin the wheel",
    description: "💰 Spin the wheel and win a random coin prize",
    type: "gambling",
    value: "25",
    price: 5,
    active: true,
    cooldownMinutes: 5,
    options: { odds: "0.1", type: "tombola" },
    purchase: "once",
    order: 550,
    limit: true,
  },
  {
    id: "tortuga",
    name: "Tortuga Roulette",
    description: "🏴‍☠️ Steal 10% of a random user's coins (up to 150)",
    type: "gambling",
    value: "",
    price: 25,
    active: true,
    cooldownMinutes: 10080,
    options: { onlyCheckedIn: false, odds: "", type: "tortuga", percent: ".1", cap: 150 },
    purchase: "once",
    order: 600,
    limit: true,
  },
  {
    id: "supertortuga",
    name: "Super Tortuga",
    description: "🏴‍☠️ Steal 20% of a random checked in user's coins (up to 400)",
    type: "gambling",
    value: "",
    price: 60,
    active: true,
    cooldownMinutes: 60,
    options: { onlyCheckedIn: true, odds: "", type: "tortuga", percent: ".2", cap: 400 },
    purchase: "once",
    order: 610,
    limit: true,
  },
  {
    id: "wizard-hat",
    name: "Wizard Hat 🧙",
    description: "Adds a wizard hat to your avatar",
    type: "hat",
    value: "/images/wizard-hat.svg",
    price: 600,
    active: true,
  },
  {
    id: "party-popper",
    name: "Party Hat 🎉",
    description: "Adds a party hat to your profile picture",
    type: "hat",
    value: "/images/party-hat.svg",
    price: 400,
    active: true,
    // On sale the week of each bro's birthday (MM-DD); add yours to the list.
    availability: { weeksOf: ["03-06", "07-10", "08-19", "10-22"], event: "Birthday" },
  },
  {
    id: "flip-out",
    name: "Flip Out",
    description: "Continuously flips your avatar upside down",
    type: "effect",
    value: "animate-flip",
    price: 400,
    active: true,
  },
  {
    id: "ghost-hat",
    name: "Ghost Hat",
    description: "Gives just your hat a ghostly, spectral glow",
    type: "effect",
    value: "effect-ghost-hat",
    price: 300,
    active: true,
  },
  {
    id: "grayscale",
    name: "Grayscale",
    description: "Drains all the colour from the spited's screen.",
    type: "spite",
    value: "spite-grayscale",
    price: 15,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 8 },
  },
  {
    id: "wobble",
    name: "Wobble",
    description: "Makes everything wobble unsettlingly.",
    type: "spite",
    value: "spite-wobble",
    price: 25,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4 },
  },
  {
    id: "comic-sans",
    name: "Comic Sans",
    description: "Swaps the spited's font to Comic Sans.",
    type: "spite",
    value: "spite-comic-sans",
    price: 15,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 8 },
  },
  {
    id: "nabla",
    name: "Nabla",
    description: "Swaps the spited's font to something wobbly and hard to read.",
    type: "spite",
    value: "spite-nabla",
    price: 50,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "jacquarda",
    name: "Jacquarda Bastarda 9 Charted",
    description: "Swaps the spited's font to something almost entirely unreadable.",
    type: "spite",
    value: "spite-jacquarda",
    price: 90,
    active: true,
    options: { durationHours: 0.25, maxDurationHours: 1 },
  },
  {
    id: "poo",
    name: "Poo",
    description: "💩 Turns the spited's screen every shade of brown, complete with a giant turd.",
    type: "spite",
    value: "spite-poo",
    price: 75,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "heaven",
    name: "Heaven",
    description: "😇 Washes the spited's screen out into a bright, heavenly white glow.",
    type: "spite",
    value: "spite-heaven",
    price: 50,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "windows95",
    name: "Windows 95",
    description: "🖥️ Gives the spited's screen an old-school grey-and-blue makeover.",
    type: "spite",
    value: "spite-windows95",
    price: 75,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2 },
  },
  {
    id: "slot-machine",
    name: "Slot Machine",
    description: "🎰 Pull the lever and match the reels for a coin prize",
    type: "gambling",
    value: "50",
    price: 5,
    active: true,
    options: { odds: "0.05", type: "slots" },
  },
  {
    id: "high-roller-slots",
    name: "High Roller Slots",
    description: "🎰 Bigger stakes, bigger reels: rarer but richer payouts",
    type: "gambling",
    value: "200",
    price: 20,
    active: true,
    cooldownMinutes: 15,
    options: { odds: "0.05", type: "slots" },
  },
  {
    id: "pint-pull",
    name: "Pint Pull",
    description: "🍺 Pull a pint: a full glass pays the full prize, a half pint pays half",
    type: "gambling",
    value: "20",
    price: 4,
    active: true,
    cooldownMinutes: 5,
    options: { odds: "0.1", type: "pint", icon: "🍺" },
  },
  {
    id: "crystal-ball",
    name: "Crystal Ball",
    description: "🔮 Gaze into the orb and let Mystic Doug reveal your fortune",
    type: "gambling",
    value: "75",
    price: 8,
    active: true,
    cooldownMinutes: 5,
    options: { odds: "0.05", type: "crystal", icon: "🔮" },
  },
  {
    id: "pub-darts",
    name: "Pub Darts",
    description: "🎯 One dart, one throw. Bullseye takes the lot",
    type: "gambling",
    value: "60",
    price: 10,
    active: true,
    cooldownMinutes: 5,
    options: { odds: "0.08", type: "darts", icon: "🎯" },
  },
  {
    id: "derby-day",
    name: "Derby Day",
    description:
      "🏇 Race your horse against punters from the pub. Whoever's horse wins takes the prize, even if it isn't yours",
    type: "gambling",
    value: "60",
    price: 15,
    active: true,
    cooldownMinutes: 10,
    // The race itself lives in functions/src/actions/derby-rules.ts.
    options: { odds: "0.12", type: "derby", icon: "🏇", runnerPrize: 8 },
  },
  {
    id: "treasure-chest",
    name: "Treasure Chest",
    description: "🏴‍☠️ Crack open the captain's chest: gold, silver, or moths",
    type: "gambling",
    value: "200",
    price: 25,
    active: true,
    cooldownMinutes: 30,
    options: { odds: "0.06", type: "chest", icon: "💰" },
  },
  {
    id: "tontine",
    name: "The Tontine",
    description:
      "⚰️ Buy in while it's signing up; once it starts nobody else can join. Miss a Thursday in the next 12 weeks and you're out. Last bro standing takes the pot",
    type: "gambling",
    value: "",
    price: 100,
    active: true,
    // The rules live in shared/tontine.ts and its length in economy.ts; the price is the stake.
    options: { type: "tontine", icon: "⚰️", anywhere: true },
    availability: weeklyDrop("2027-01-07"),
  },
  // Weekly puzzles: one answer for everyone, a week to crack it. The price is
  // the entry fee, which all goes in the pot; the rules live in
  // shared/puzzles.ts and each game's puzzle-*.ts.
  {
    id: "the-usual",
    name: "The Usual",
    description:
      "🍻 Diane (or one of the twins) wants the usual, and won't say what it is. Work out the round in 7 goes. Solvers split the pot, and if nobody gets it, it rolls over",
    type: "gambling",
    value: "",
    price: 10,
    active: true,
    options: { type: "puzzle", game: "order", icon: "🍻", anywhere: true },
    availability: weeklyDrop("2026-10-22"),
  },
  {
    id: "pub-detective",
    name: "Pub Detective",
    description:
      "🕵️ Find this week's mystery pub from the bros' own dirty laundry. 6 guesses, a new clue each time. Solvers split the pot, and if nobody gets it, it rolls over",
    type: "gambling",
    value: "",
    price: 15,
    active: true,
    options: { type: "puzzle", game: "detective", icon: "🕵️", anywhere: true },
    availability: weeklyDrop("2026-12-24"),
  },
  {
    id: "whodunnit",
    name: "Whodunnit",
    description:
      "🔎 A mystery stat, a lineup of bros, 4 accusations. Solvers split the pot, and if nobody gets it, it rolls over",
    type: "gambling",
    value: "",
    price: 10,
    active: true,
    options: { type: "puzzle", game: "whodunnit", icon: "🔎", anywhere: true },
    availability: weeklyDrop("2026-12-31"),
  },
  // Chaos items: outcome tables live in functions/src/actions/chaos-rules.ts,
  // keyed by id.
  {
    id: "pickpocket",
    name: "Pickpocket",
    description:
      "🧤 Try to lift a few coins off a random player. Get caught and you pay them instead",
    type: "gambling",
    value: "",
    price: 10,
    active: true,
    cooldownMinutes: 60,
    options: { type: "chaos", icon: "🧤", hint: "17% steal • 12% caught • 1% GRAND HEIST" },
  },
  {
    id: "robin-hood",
    name: "Robin Hood",
    description: "🏹 Rob the richest player to give to the poorest. You keep a small cut",
    type: "gambling",
    value: "",
    price: 15,
    active: true,
    cooldownMinutes: 180,
    options: { type: "chaos", icon: "🏹", hint: "24% strike • 1% LEGENDARY" },
  },
  {
    id: "pub-chaos",
    name: "Pub Chaos",
    description:
      "🌪️ Unleash a random pub event. Taxmen, seagulls, lost wallets… anything could happen",
    type: "gambling",
    value: "",
    price: 20,
    active: true,
    cooldownMinutes: 120,
    options: { type: "chaos", icon: "🌪️", hint: "25% something happens • 1% JACKPOT" },
  },
  // Power-ups: handled by handleUtilPurchase in functions/src/actions/shop.ts, keyed by id.
  // The coin-earning ones are worked out in shared/powerups.ts and tuned in
  // economy.ts. Each is a bet on how the night goes (how long you stay, how
  // many pubs you get round, how many bros turn up) and can lose you coin,
  // and each can only be bought once a week. The earning ones cost hours of
  // your own coin rate (`options.priceHours`, see getItemPrice in
  // shop-utils.ts), since that's what they pay out on.
  {
    id: "happyhour",
    name: "Happy Hour 🍻",
    description:
      "Call a happy hour for the next hour of your check-in. The more bros in the pub with you, the more it pays: up to ×5. Drink alone and you lose the hour's coin. Settled in the morning",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 15,
    active: true,
    oncePerWeek: true,
    options: { priceHours: 0.75, icon: "🍻", hint: "Alone ×0 • 1 bro ×1 • 2 bros ×2 … 5 bros ×5" },
    availability: weeklyDrop("2026-10-15"),
  },
  {
    id: "snowball",
    name: "Snowball ☃️",
    description:
      "Starts as a little snowball (×0.75) and grows every hour you stay in this pub, paying for itself after two. Leave early and it melts away",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 15,
    active: true,
    oncePerWeek: true,
    options: {
      priceHours: 1,
      icon: "☃️",
      hint: "×0.75 → ×1.25 → ×1.75 … ×3.5 until you check out",
    },
    availability: weeklyDrop("2026-11-12"),
  },
  {
    id: "mysterypint",
    name: "Mystery Pint 🎰",
    description:
      "Neck a mystery pint and roll a multiplier for the rest of your check-in. Could be ×5… could be flat",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 15,
    active: true,
    oncePerWeek: true,
    options: { priceHours: 0.75, icon: "🎰", hint: "×0.5 to ×5 • 3% chance of ×5" },
    availability: weeklyDrop("2026-11-05"),
  },
  {
    id: "pubcrawl",
    name: "Pub Crawl 🗺️",
    description:
      "Go on a crawl from now until the end of the night: the more pubs you spend half an hour in from here on, the more it pays, up to ×3. Pubs you've already been to don't count. Stay put and it halves what you make. Settled in the morning",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 20,
    active: true,
    oncePerWeek: true,
    options: { priceHours: 1.5, icon: "🗺️", hint: "1 pub ×0.5 • 2 ×1 • 3 ×1.5 … 6 ×3" },
    availability: weeklyDrop("2026-11-26"),
  },
  {
    id: "lastorders",
    name: "Last Orders 🔔",
    description:
      "Bet on being the last one standing tonight: ×3 for last out, and leaving within 15 minutes of the last one out counts. The more people still out after you, the less it pays, down to halving what you make. Needs at least three others out. Settled in the morning",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 15,
    active: true,
    oncePerWeek: true,
    options: {
      priceHours: 1,
      icon: "🔔",
      hint: "Last out (give or take 15 mins) ×3 • 2nd ×1.5 • 3rd ×1 • otherwise ×0.5",
    },
    availability: weeklyDrop("2026-12-17"),
  },
  {
    id: "whip",
    name: "The Whip 💷",
    description:
      "Start a whip for the rest of the night: every bro who drinks with you for half an hour, in any pub, chips in, up to ×3. Nobody turns up and you lose the lot. Settled in the morning",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 15,
    active: true,
    oncePerWeek: true,
    options: { priceHours: 1, icon: "💷", hint: "No bros ×0 • 2 ×1 • 4 ×2 • 6 ×3" },
    availability: weeklyDrop("2026-12-03"),
  },
  {
    id: "pubgolf",
    name: "Pub Golf ⛳",
    description:
      "Play a round for the rest of the night: every pub where you and another bro both spend half an hour is a hole sunk, up to ×3.5. No holes and you lose the lot. Settled in the morning",
    type: "util",
    value: "",
    // The least it costs: it's `priceHours` of your coin rate (see getItemPrice).
    price: 20,
    active: true,
    oncePerWeek: true,
    options: { priceHours: 1.5, icon: "⛳", hint: "0 holes ×0 • 2 ×1 • 3 ×2 • 4 ×3 • 5 ×3.5" },
    availability: weeklyDrop("2026-12-10"),
  },
  {
    id: "padlock",
    name: "Padlock 🔒",
    description:
      "Lock up your coins for 12 hours: nobody can Tortuga, pickpocket or chaos you (you sit out their windfalls too)",
    type: "util",
    value: "",
    price: 75,
    active: true,
    oncePerWeek: true,
    options: { icon: "🔒", hint: "Lasts 12 hours" },
    availability: weeklyDrop("2026-11-19"),
  },
  {
    id: "sicknote",
    name: "Sick Note 🤒",
    description:
      "Can't make it this week? Hand in a sick note: your streak survives and you dodge the missed-week fine. One every 4 weeks, and each one this year costs double the last",
    type: "util",
    value: "",
    price: 100,
    active: true,
    cooldownMinutes: 40320,
    options: { icon: "🤒" },
    availability: weeklyDrop("2026-10-29"),
  },
  // Spite defences (see spite-defence.ts). Priced against the spites they
  // beat: scrubbing one off costs about what a mid-range spite does, a shield
  // for a whole night out costs less than a regular earns that night, and
  // fighting back (rebound, return to sender) costs more than just hiding.
  // Guards are secret until they go off; then everyone hears about it.
  {
    id: "spite-scrub",
    name: "Spite Scrubber 🧽",
    description: "Scrubs off the last spite cast on you",
    type: "defence",
    value: "",
    price: 25,
    active: true,
    options: { icon: "🧽" },
    availability: weeklyDrop("2026-11-12"),
  },
  {
    id: "spite-cleanse",
    name: "Full Cleanse ✨",
    description: "Scrubs off every spite on you at once",
    type: "defence",
    value: "",
    price: 60,
    active: true,
    options: { icon: "✨" },
    availability: weeklyDrop("2026-11-12"),
  },
  {
    id: "spite-shield",
    name: "Spite Shield 🛡️",
    description: "Blocks every spite cast at you while it's up, and they still pay for it",
    type: "defence",
    value: "",
    price: 25,
    active: true,
    options: { icon: "🛡️", durationHours: 1, maxDurationHours: 8 },
    availability: weeklyDrop("2026-11-26"),
  },
  {
    id: "spite-scapegoat",
    name: "Scapegoat 🐐",
    description: "Pick a scapegoat: every spite cast at you lands on them instead",
    type: "defence",
    value: "",
    price: 35,
    active: true,
    options: { icon: "🐐", durationHours: 1, maxDurationHours: 4 },
    availability: weeklyDrop("2026-11-26"),
  },
  {
    id: "spite-boomerang",
    name: "Boomerang 🪃",
    description: "Every spite cast at you flies straight back at whoever cast it",
    type: "defence",
    value: "",
    price: 40,
    active: true,
    options: { icon: "🪃", durationHours: 1, maxDurationHours: 4 },
    availability: weeklyDrop("2026-11-26"),
  },
  {
    id: "spite-return-to-sender",
    name: "Return to Sender 📬",
    description: "Sends every spite on you back to whoever cast it, for however long it had left",
    type: "defence",
    value: "",
    price: 75,
    active: true,
    options: { icon: "📬" },
    availability: weeklyDrop("2026-11-26"),
  },
  {
    id: "spite-bomb-squad",
    name: "Bomb Squad 🧯",
    description: "Sweep a pub for booby traps and defuse any you find",
    type: "defence",
    value: "",
    price: 20,
    active: true,
    options: { icon: "🧯" },
    availability: weeklyDrop("2026-12-10"),
  },
  {
    id: "spite-boobytrap",
    name: "Booby Trap 💣",
    description:
      "Rig a pub with a spite of your choice: the next person to check in there gets it, unless they're protected. Stays armed for 24 hours",
    type: "defence",
    value: "",
    // The trap itself; the spite it's loaded with costs extra.
    price: 40,
    active: true,
    options: { icon: "💣" },
    availability: weeklyDrop("2026-12-10"),
  },
  {
    id: "spite-amnesty",
    name: "Spite Amnesty 🕊️",
    description: "Lifts every active spite on everyone. Peace in our time",
    type: "defence",
    value: "",
    price: 250,
    active: true,
    cooldownMinutes: 10080,
    options: { icon: "🕊️" },
    availability: weeklyDrop("2026-12-24"),
  },
  {
    id: "bail",
    name: "Bail 🔑",
    description: "Posts your bail: gets you out of jail right now",
    type: "defence",
    value: "",
    // More than the shortest sentence (not worth bailing out of) but far
    // less than an hour inside.
    price: 100,
    active: true,
    options: { icon: "🔑" },
    availability: weeklyDrop("2026-10-22"),
  },
  // Curses: spites with teeth (see curses.ts). As well as getting in the
  // way, each one changes how its victim earns, gambles or dresses while it
  // lasts. Priced like any spite (by how much it hurts), but above the
  // purely visual ones, since their effects outlast the screen going back to
  // normal; the ones that touch coin (and the Kick Me sign, which makes every
  // other spite cheaper) cost the most and can't be stretched as far. Every
  // one only ever burns coin or moves it between players, never mints it.
  {
    id: "jail",
    name: "Jail 🚔",
    description:
      "Locks them up behind bars: no pub coin, no bonuses and no casino until they're out",
    type: "spite",
    value: "spite-jail",
    price: 75,
    active: true,
    options: { durationHours: 0.25, maxDurationHours: 1, curse: "jail", icon: "🚔" },
    availability: weeklyDrop("2026-10-22"),
  },
  {
    id: "black-cat",
    name: "Black Cat 🐈‍⬛",
    description: "A black cat crosses their path: half their casino wins vanish into thin air",
    type: "spite",
    value: "spite-black-cat",
    price: 40,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, curse: "jinx", icon: "🐈‍⬛" },
    availability: weeklyDrop("2026-10-29"),
  },
  {
    id: "flat-pint",
    name: "Flat Pint 🫗",
    description: "Every pint goes flat: they earn half the usual pub coin",
    type: "spite",
    value: "spite-flat-pint",
    price: 40,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, curse: "flatPint", icon: "🫗" },
    availability: weeklyDrop("2026-11-12"),
  },
  {
    id: "leech",
    name: "Leech 🧛",
    description:
      "Latch on and drain half the pub coin they earn into your pocket (up to 100 coins)",
    type: "spite",
    value: "spite-leech",
    price: 50,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 2, curse: "leech", icon: "🧛" },
    availability: weeklyDrop("2026-12-03"),
  },
  {
    id: "loan-shark",
    name: "Loan Shark 🦈",
    description: "You take a quarter of everything they win at the casino (up to 100 a win)",
    type: "spite",
    value: "spite-loan-shark",
    price: 40,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, curse: "loanShark", icon: "🦈" },
    availability: weeklyDrop("2026-12-03"),
  },
  {
    id: "wardrobe-malfunction",
    name: "Wardrobe Malfunction 🩲",
    description:
      "Strips off their hat and effects, and they can't put them back on until it's over",
    type: "spite",
    value: "spite-wardrobe",
    price: 30,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, curse: "wardrobe", icon: "🩲" },
    availability: weeklyDrop("2026-11-19"),
  },
  {
    id: "clown-wig",
    name: "Clown Wig 🤡",
    description: "Glues a clown wig to their head. They can't take it off until it wears off",
    type: "spite",
    value: "spite-clown",
    price: 35,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, curse: "clown", icon: "🤡" },
    availability: weeklyDrop("2026-11-19"),
  },
  {
    id: "kick-me",
    name: "Kick Me Sign 🦶",
    description: "Sticks a sign on their back: every spite cast at them costs half price",
    type: "spite",
    value: "spite-kick-me",
    price: 40,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, curse: "kickMe", icon: "🦶" },
    availability: weeklyDrop("2026-12-31"),
  },
  {
    id: "beer-goggles",
    name: "Beer Goggles 🥽",
    description: "Gives them double vision. Everyone looks gorgeous, nothing is readable",
    type: "spite",
    value: "spite-beer-goggles",
    price: 25,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 4, icon: "🥽" },
    availability: weeklyDrop("2026-10-15"),
  },
  {
    id: "rain-cloud",
    name: "Rain Cloud 🌧️",
    description: "A little rain cloud follows them around, drizzling on everything",
    type: "spite",
    value: "spite-rain-cloud",
    price: 15,
    active: true,
    options: { durationHours: 0.5, maxDurationHours: 8, icon: "🌧️" },
    availability: weeklyDrop("2026-10-15"),
  },
  // Instant pranks: handled by handleCursePurchase in
  // functions/src/actions/curses.ts, keyed by id. Spite defences work on
  // these too: a shield stops them, a boomerang turns them on the buyer.
  {
    id: "freaky-friday",
    name: "Freaky Friday 🔄",
    description: "Swap outfits with someone: you get their hat and effects, they get yours",
    type: "curse",
    value: "",
    price: 75,
    active: true,
    cooldownMinutes: 360,
    options: { icon: "🔄" },
    availability: weeklyDrop("2026-12-17"),
  },
  {
    id: "cat-burglar",
    name: "Cat Burglar 🦹",
    description:
      "Break into their wardrobe and nick one of their looks. Get caught and you pay them 50 and do 30 minutes inside",
    type: "curse",
    value: "",
    price: 125,
    active: true,
    cooldownMinutes: 1440,
    options: { icon: "🦹", hint: "40% steal • 30% caught • prestige hats are too well guarded" },
    availability: weeklyDrop("2026-12-17"),
  },
  {
    id: "your-round",
    name: "Your Round 🍻",
    description:
      "Make them buy a round: they pay 10 coins to everyone else in their pub (up to 6 people)",
    type: "curse",
    value: "",
    price: 50,
    active: true,
    cooldownMinutes: 120,
    options: { icon: "🍻" },
    availability: weeklyDrop("2026-12-24"),
  },
  {
    id: "police-raid",
    name: "Police Raid 🚨",
    description:
      "Tip off the police: everyone at the busiest pub (except you) does 15 minutes in jail",
    type: "curse",
    value: "",
    price: 250,
    active: true,
    cooldownMinutes: 1440,
    options: { icon: "🚨" },
    availability: weeklyDrop("2026-11-05"),
  },
  {
    id: "snitch",
    name: "Snitch 🐀",
    description: "Find out what spite defence someone's secretly got up. They'll never know",
    type: "curse",
    value: "",
    price: 25,
    active: true,
    options: { icon: "🐀" },
    availability: weeklyDrop("2026-12-31"),
  },
  // Prestige hats: long-term goals that take a regular most of a year or more.
  {
    id: "unicorn-hat",
    name: "Unicorn 🦄",
    description:
      "A shimmering spiral horn, a rainbow mane and a flower crown. Rare, majestic, and proof you've put the hours in",
    type: "hat",
    value: "/images/unicorn-hat.svg",
    price: 7500,
    active: true,
  },
  {
    id: "diamond-hat",
    name: "Diamond 💎",
    description:
      "A brilliant-cut diamond, glinting on a platinum crown in a burst of light. For the pub-goer who has everything",
    type: "hat",
    value: "/images/diamond-hat.svg",
    price: 12000,
    active: true,
  },
  {
    id: "big-cheese-hat",
    name: "The Big Cheese 🧀",
    description:
      "A whole wedge of cheese, a golden crown and a resident mouse. The most expensive hat in the shop. Everyone will know who's in charge",
    type: "hat",
    value: "/images/big-cheese-hat.svg",
    price: 20000,
    active: true,
  },
  {
    id: "strawberry-head",
    name: "Strawberry Head 🍓",
    description:
      "Swaps your head for a giant blinking strawberry that someone keeps taking bites out of",
    type: "hat",
    value: "/images/strawberry-head.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-10-22"),
  },
  {
    id: "poo-head",
    name: "Poo Head 💩",
    description:
      "Turns your head into a grinning, wobbling poo, complete with flies and stink lines",
    type: "hat",
    value: "/images/poo-head.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-10-29"),
  },
  {
    id: "camel-hat",
    name: "Camel 🐪",
    description:
      "A camel lies across your head chewing the cud, fluttering its lashes and spitting over the side",
    type: "hat",
    value: "/images/camel-hat.svg",
    price: 200,
    active: true,
    conditions: { kmFromCardiff: { min: 1000 } },
  },
  {
    id: "dog-face",
    name: "Dog Face 🐶",
    description:
      "Floppy flapping ears, a sniffing nose, a panting tongue that drools, and a collar with a jingling tag",
    type: "hat",
    value: "/images/dog-face.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-10-08"),
  },
  {
    id: "cat-face",
    name: "Cat Face 🐱",
    description:
      "Twitching ears, quivering whiskers, a swishing tail, and a mouse that keeps daring to run past",
    type: "hat",
    value: "/images/cat-face.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-10-15"),
  },
  {
    id: "coin-top-hat",
    name: "Pub Coin Top Hat 🎩",
    description:
      "A top hat that grows a pixel taller for every four pub coin you hold. Spend it and it shrinks",
    type: "hat",
    value: COIN_TOP_HAT,
    price: 7500,
    active: true,
    availability: weeklyDrop("2026-11-05"),
  },
  {
    id: "pint-tower",
    name: "Pint Tower 🍻",
    description:
      "A wobbly tower of pints balanced on your head, with another stacked on for every Thursday in your streak. Miss one and it all comes crashing down",
    type: "hat",
    value: STREAK_TOP_HAT,
    price: 7500,
    active: true,
    availability: weeklyDrop("2027-01-07"),
  },
  {
    id: "pub-beard",
    name: "Pub Beard 🧔",
    description:
      "A plaited beard that grows longer with every hour you've ever spent in the pub on a Thursday. It never gets trimmed",
    type: "hat",
    value: PUB_BEARD,
    price: 7500,
    active: true,
    availability: weeklyDrop("2027-01-14"),
  },
  {
    id: "nest-beard",
    name: "Nesting Beard 🧔",
    description:
      "An enormous ginger beard that sways in the breeze, with a little bird living in it that pops up to sing",
    type: "hat",
    value: "/images/nest-beard.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-12-03"),
  },
  {
    // Was Moustache Roulette; the id is kept so owners keep it. See LEGACY_HATS.
    id: "moustache-roulette",
    name: "Movember Moustache 🥸",
    description:
      "A blond handlebar moustache that reaches further out with every Thursday hour this month, then gets shaved off on the 1st. Only on sale in November",
    type: "hat",
    value: MONTHLY_MOUSTACHE,
    price: 7500,
    active: true,
    availability: { from: "11-01", to: "11-30", event: "Movember" },
  },
  {
    id: "mutton-chops",
    name: "Mutton Chops 🎩",
    description:
      "Bristling Victorian sideburns, bushy eyebrows and a monocle that pops out in shock",
    type: "hat",
    value: "/images/mutton-chops.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-11-26"),
  },
  {
    id: "pint-helmet",
    name: "Pint Helmet 🍺",
    description:
      "A hard hat with a pint strapped either side, piped straight to your mouth. The ultimate pub-goer's headgear. Glug glug glug",
    type: "hat",
    value: "/images/pint-helmet.svg",
    price: 5000,
    active: true,
    availability: weeklyDrop("2026-12-24"),
  },
  {
    id: "atomic-wedgie",
    name: "Atomic Wedgie 🩲",
    description:
      "Heart-print Y-fronts yanked up over your head, hoiked ever higher until they snap back down. TWANG",
    type: "hat",
    value: "/images/atomic-wedgie.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-12-10"),
  },
  {
    id: "cigarette",
    name: "Cigarette 🚬",
    description:
      "A fag dangling off your lip, its tip glowing with every drag while you blow smoke rings",
    type: "hat",
    value: "/images/cigarette.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-11-12"),
  },
  {
    id: "vape",
    name: "Vape 💨",
    description:
      "A long draw on a vape pen, then a colossal candy-coloured cloud that swallows your whole head",
    type: "hat",
    value: "/images/vape.svg",
    price: 200,
    active: true,
    availability: weeklyDrop("2026-11-19"),
  },
  {
    id: "dooby",
    name: "Dooby 🌿",
    description:
      "A fat cone hanging off your lip, lazy smoke, heavy bloodshot eyelids and the munchies floating past",
    type: "hat",
    value: "/images/dooby.svg",
    price: 420,
    active: true,
    conditions: { hours: { from: "16:20", to: "17:20" } },
  },
  {
    id: "mooning",
    name: "Mooning 🍑",
    description:
      "Swaps your face for a bare bum with its jeans round its hips, twerking side to side and letting rip the odd green PFFT",
    type: "hat",
    value: "/images/mooning.svg",
    price: 300,
    active: true,
    options: { novelty: true },
    conditions: { days: [5, 6], hours: { from: "21:00", to: "03:00" } },
  },
  {
    id: "talking-arse",
    name: "Talking Out Your Arse 🗣️",
    description:
      "Your face becomes a bum whose puckered ring jabbers away: 'Trust me, mate', 'I could've gone pro', 'Just the one, honest'",
    type: "hat",
    value: "/images/talking-arse.svg",
    price: 300,
    active: true,
    options: { novelty: true },
    availability: weeklyDrop("2026-12-17"),
  },
  {
    id: "manneken-piss",
    name: "Manneken Piss 💦",
    description:
      "A smug little cherub stands on your head, weeing an endless golden arc down the side of your face into a splashing puddle",
    type: "hat",
    value: "/images/manneken-piss.svg",
    price: 300,
    active: true,
    options: { novelty: true },
    conditions: { kmFromCardiff: { min: 500 } },
  },
  {
    id: "piss-head",
    name: "Piss Head 🥴",
    description:
      "Absolutely steaming: a nicked traffic cone on your head, stars and birds spinning round it, a glowing red nose and the hiccups",
    type: "hat",
    value: "/images/piss-head.svg",
    price: 250,
    active: true,
    options: { novelty: true },
    conditions: { hours: { from: "23:00", to: "03:00" } },
  },
  {
    id: "oktoberfest-hat",
    name: "Oktoberfest 🥨",
    description:
      "A Bavarian hat with a springing tuft and a pretzel swinging off the brim, and a frothy stein hoisted for a Prost! This week only",
    type: "hat",
    value: "/images/oktoberfest.svg",
    price: 300,
    active: true,
    availability: { from: "2026-10-05", to: "2026-10-11", event: "Oktoberfest" },
  },
  {
    id: "matador-hat",
    name: "Matador 🌹",
    description:
      "A matador's montera with swinging pompoms, a rose between your teeth, clacking castanets and a swishing cape. ¡Olé! One night only",
    type: "hat",
    value: "/images/matador.svg",
    price: 300,
    active: true,
    availability: { from: "2026-10-29", to: "2026-10-29", event: "Spanish Night" },
  },
];

const SHOP_ITEMS_BY_ID = new Map(SHOP_ITEMS.map((item) => [item.id, item]));

export function getShopItem(id: string): ShopItem | undefined {
  return SHOP_ITEMS_BY_ID.get(id);
}
